import { softGateStyle } from '@chegg-wt/core/state/currentProject/actions'
import FormLayout, { FormPageTop } from '@chegg-wt/core/components/Layouts/Form'
import { FetchCitationsPayload } from '@chegg-wt/core/state/bibliography/types'
import ConfirmLayout, { ConfirmPageTop } from '@chegg-wt/core/components/Layouts/Confirm'
import { TopContentWrapper } from '@chegg-wt/core/components/AdFrame/'
import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import SEOTitle from '@chegg-wt/core/components/SEOTitle'
import { AppState } from '@chegg-wt/core/state'
import {
  getLvpLayout,
  useLvpContext,
} from '@chegg-wt/core/components/Layouts/LargeVideoPlayerLayout'
import { NextPageWithLayout } from '@chegg-wt/core/types/layout'
import { FORM_PAGE } from '@chegg-wt/core/utils/routes'
import { connect } from 'react-redux'
import { getUrlStyle } from '@chegg-wt/core/server/utils/redirect'
import { getSourceTypeName } from '@chegg-wt/core/utils/sourceTypes'
import { flowed } from '@chegg-wt/core/state/app/actions'
import { isEditCitationSelector } from '@chegg-wt/core/state/citation/selectors'
import {
  currentStyleSelector,
  shouldShowStyleGatedModalSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import { lvpLayoutSelector } from '@chegg-wt/core/state/featureFlags/selectors'
import { getCapitalizedSEONameOfStyle } from '@chegg-wt/core/utils/styles'
import { useOnUnloadTimeOnPage } from '@chegg/time-on-page'
import { getSourceFromQuery } from '../../../utils/routes'
import { confirmContent, formB1 } from '../../../copy/CMSContent'

interface FormProps {
  style: string
  isEditCitation: boolean
  flowed: (route: string) => void
  softGateStyle: typeof softGateStyle
  shouldShowStyleGatedModal: boolean
  fetchCurrentProject: (payload: FetchCitationsPayload) => void
  isLvp: boolean
}

const NewPage: React.FC<FormProps> = ({
  style,
  isEditCitation,
  flowed,
  softGateStyle,
  shouldShowStyleGatedModal,
  isLvp,
}) => {
  const router = useRouter()
  const { source: sourceQuery } = router.query as { style: string; source: string }
  const source = getSourceFromQuery(router, sourceQuery)
  const formattedStyle = getCapitalizedSEONameOfStyle(getUrlStyle(style))

  useEffect(() => {
    if (isEditCitation) flowed('toEditCitation')
  }, [])
  useOnUnloadTimeOnPage()
  useEffect(() => {
    if (shouldShowStyleGatedModal) softGateStyle(undefined, false) // compare current project style with default allowed style
  }, [style, shouldShowStyleGatedModal])

  return (
    <>
      <SEOTitle
        title={`Citation Form | ${formattedStyle} | ${getSourceTypeName(
          source
        )} | Cite This For Me`}
      />
      {!isLvp && (
        <TopContentWrapper>
          <FormPageTop />
        </TopContentWrapper>
      )}
      <FormLayout route="toNewCitation" formType="new" copy={formB1} />
    </>
  )
}

const ConfirmPage: React.FC<FormProps> = ({ style, isLvp }) => {
  const router = useRouter()
  const { source: sourceQuery } = router.query as { style: string; source: string }
  const source = getSourceFromQuery(router, sourceQuery)
  const formattedStyle = getCapitalizedSEONameOfStyle(getUrlStyle(style))

  return (
    <>
      <SEOTitle
        title={`Citation Evaluation | ${formattedStyle} | ${getSourceTypeName(
          source
        )} | Cite This For Me`}
      />
      {!isLvp && (
        <TopContentWrapper>
          <ConfirmPageTop />
        </TopContentWrapper>
      )}
      <ConfirmLayout copy={confirmContent} />
    </>
  )
}

const CustomPage: React.FC<FormProps> = ({
  style,
  isLvp,
  softGateStyle,
  shouldShowStyleGatedModal,
}) => {
  const router = useRouter()
  const { source: sourceQuery } = router.query as { style: string; source: string }
  const source = getSourceFromQuery(router, sourceQuery)
  const formattedStyle = getCapitalizedSEONameOfStyle(getUrlStyle(style))

  useEffect(() => {
    if (shouldShowStyleGatedModal) softGateStyle(undefined, false) // compare current project style with default allowed style
  }, [style, shouldShowStyleGatedModal])

  return (
    <>
      <SEOTitle
        title={`Citation Form | ${formattedStyle} | ${getSourceTypeName(
          source
        )} | Cite This For Me`}
      />
      {!isLvp && (
        <TopContentWrapper>
          <FormPageTop />
        </TopContentWrapper>
      )}
      <FormLayout route="toManualCitation" formType="manual" copy={formB1} />
    </>
  )
}

const FormPage: NextPageWithLayout<FormProps> = (props) => {
  const { currentPage } = useLvpContext()

  switch (currentPage) {
    case FORM_PAGE.NEW:
      return <NewPage {...props} />
    case FORM_PAGE.EVAL:
      return <ConfirmPage {...props} />
    case FORM_PAGE.MANUAL:
      return <CustomPage {...props} />
    default:
      return null
  }
}

FormPage.getPageLayout = (page) =>
  getLvpLayout(page, () => {
    const { currentPage } = useLvpContext()

    switch (currentPage) {
      case FORM_PAGE.NEW:
        return <FormPageTop />
      case FORM_PAGE.EVAL:
        return <ConfirmPageTop />
      case FORM_PAGE.MANUAL:
        return <FormPageTop />
      default:
        return null
    }
  })

const mapStateToProps = (state: AppState) => ({
  style: currentStyleSelector(state),
  isEditCitation: isEditCitationSelector(state),
  isLvp: lvpLayoutSelector(state),
  shouldShowStyleGatedModal: shouldShowStyleGatedModalSelector(state),
})

const mapDispatchToProps = {
  flowed,
  softGateStyle,
}

export default connect(mapStateToProps, mapDispatchToProps)(FormPage)
